import { Box, CSS } from '@project44/manifest-ds';

import BreadcrumbComponent from './Breadcrumb';

export interface Breadcrumb {
  label: string;
  href?: string;
  onClick?: () => void;
  css?: CSS;
}

interface Props {
  crumbs: Breadcrumb[];
  colorMode?: 'dark' | 'light';
  css?: CSS;
}

/**
 * Breadcrumbs
 * @param prop.data.label Label of the breadcrumb
 * @param prop.data.href Pass href to breadcrumb config if you want to go to different app URL
 * @param prop.data.onClick Pass onClick to breadcrumb config if you i.e. want to go to same app URL (with history)
 * @param prop.data.css Style of a single Breadcrumb
 * @param prop.fontStyle Font style of breadcrumbs and slashes between them
 * @returns Breadcrumbs component
 */
const Breadcrumbs: React.FC<Props> = ({ crumbs, colorMode, css }) => {
  const isDarkText = colorMode === 'dark';
  const commonStyles: CSS = {
    color: isDarkText ? '$text-primary' : '$text-contrast',
    fontWeight: 'semibold',
  };

  const breadcrumbs = [...crumbs];
  const bottomCrumb = breadcrumbs.pop();

  return (
    <Box
      data-testid="breadcrumbs-container"
      css={{ textAlign: 'left', alignSelf: 'center', ...css }}
    >
      {breadcrumbs.map((d, i) => (
        <BreadcrumbComponent
          key={i}
          label={`${d.label} /`}
          href={d.href}
          onClick={d.onClick}
          css={{
            ...commonStyles,
            color: isDarkText ? '$text-tertiary' : '$text-contrast',
            fontSize: '$small',
            mr: 5,
          }}
        />
      ))}
      {bottomCrumb && (
        <BreadcrumbComponent
          label={bottomCrumb.label}
          href={bottomCrumb.href}
          onClick={bottomCrumb.onClick}
          css={{
            ...commonStyles,
            fontSize: '$x-large',
            display: `${breadcrumbs.length > 0 ? 'block' : 'inline'}`,
          }}
        />
      )}
    </Box>
  );
};

export default Breadcrumbs;
